import { useTheme, Box, Button, Container, Divider, Stack, Typography } from "@mui/material";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";

export default function Footer() {
    const { t } = useTranslation();
    const theme = useTheme();

    const contactEmail = t("common.footer.email");
    const contactAddress = t("common.footer.address");
    
    const copyText = async (value: string, successMessage: string) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
                toast.success(successMessage);
                return;
            }
            const textarea = document.createElement("textarea");
            textarea.value = value;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(textarea);
            if (ok) {
                toast.success(successMessage);
            } else {
                toast.error(t("common.footer.copyError"));
            }
        } catch {
            toast.error(t("common.footer.copyError"));
        }
    };

    const quickLinks = [
        { label: t("common.nav.services"), to: "/leistungen" },
        { label: t("common.nav.testimonials"), to: "/referenzen" },
        { label: t("common.nav.about"), to: "/ueber-uns" },
        { label: t("common.nav.faq", "FAQ"), to: "/faq" },
        { label: t("common.nav.contact"), to: "/kontakt" }
    ];

    return (
        <Box component="footer" sx={{ mt: { xs: 4, md: 8 }, pt: { xs: 6, md: 8 }, pb: 4, borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
            <Container>
                <Box
                    sx={{
                        display: "grid",
                        gap: { xs: 5, md: 4 },
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.2fr 1fr 1fr" },
                        alignItems: "start",
                    }}
                >
                    {/* Unternehmen Info */}
                    <Stack spacing={2.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600 }}>
                            Unternehmen
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <img src={theme.palette.mode === "dark" ? "/logo_transparent_darkmode.png" : "/logo_transparent.png"} alt="RSS-Energie Logo" style={{ height: 48, width: "auto", objectFit: "contain" }} />
                        </Box>
                        <Stack spacing={1.5}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => copyText(contactAddress, t("common.footer.copyAddressSuccess"))}
                                aria-label={t("common.footer.copyAddressAria")}
                                sx={{
                                    border: "none",
                                    background: "none",
                                    p: 0,
                                    textAlign: "left",
                                    color: "text.secondary",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                    transition: "color 0.2s",
                                    "&:hover": { color: "var(--accent)" },
                                }}
                            >
                                <LocationOnRoundedIcon fontSize="small" sx={{ opacity: 0.8, mt: "2px" }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{t("common.footer.address")}</Typography>
                            </Box>

                            <Box
                                component="button"
                                type="button"
                                onClick={() => copyText(contactEmail, t("common.footer.copyEmailSuccess"))}
                                aria-label={t("common.footer.copyEmailAria")}
                                sx={{
                                    border: "none",
                                    background: "none",
                                    p: 0,
                                    textAlign: "left",
                                    color: "text.secondary",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    transition: "color 0.2s",
                                    "&:hover": { color: "var(--accent)" },
                                }}
                            >
                                <EmailRoundedIcon fontSize="small" sx={{ opacity: 0.8 }} />
                                <Typography variant="body2">{t("common.footer.email")}</Typography>
                            </Box>
                        </Stack>
                    </Stack>

                    {/* Schnellzugriff */}
                    <Stack spacing={2.5} alignItems="flex-start" sx={{ display: { xs: "none", md: "flex" } }}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600 }}>
                            Schnellzugriff
                        </Typography>
                        <Stack spacing={0.5} alignItems="flex-start">
                            {quickLinks.map((link) => (
                                <Button 
                                    key={link.to}
                                    component={RouterLink} 
                                    to={link.to} 
                                    color="inherit" 
                                    startIcon={<ChevronRightRoundedIcon sx={{ opacity: 0.5 }} />}
                                    sx={{ 
                                        px: 0, 
                                        minWidth: 0, 
                                        color: "text.secondary", 
                                        fontWeight: 400,
                                        "&:hover": { 
                                            color: "var(--accent)", 
                                            background: "transparent",
                                            "& .MuiButton-startIcon": { transform: "translateX(4px)", opacity: 1, color: "var(--accent)" }
                                        },
                                        "& .MuiButton-startIcon": { transition: "all 0.2s ease" }
                                    }}
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </Stack>
                    </Stack>

                    {/* Rechtliches */}
                    <Stack spacing={2.5} alignItems="flex-start">
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600 }}>
                            Rechtliches
                        </Typography>
                        <Stack spacing={1} alignItems="flex-start">
                            <Button 
                                component={RouterLink} 
                                to="/datenschutz" 
                                color="inherit" 
                                startIcon={<SecurityRoundedIcon sx={{ opacity: 0.7 }} />}
                                sx={{ px: 0, minWidth: 0, color: "text.secondary", fontWeight: 400, "&:hover": { color: "var(--accent)", background: "transparent" } }}
                            >
                                {t("common.nav.privacy")}
                            </Button>
                            <Button 
                                component={RouterLink} 
                                to="/impressum" 
                                color="inherit" 
                                startIcon={<InfoRoundedIcon sx={{ opacity: 0.7 }} />}
                                sx={{ px: 0, minWidth: 0, color: "text.secondary", fontWeight: 400, "&:hover": { color: "var(--accent)", background: "transparent" } }}
                            >
                                {t("common.nav.imprint")}
                            </Button>
                        </Stack>
                        
                    </Stack>
                </Box>
                
                <Divider sx={{ mt: 5, mb: 3 }} />
                
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {t("common.footer.rights")}
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
